import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === "true";

    if (animationEnabled && !event.target.closest(".quick-add-modal")) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    // Marque de performance de début (évite DOMException si non créée avant measure)
    try {
      cartPerformance.createStartingMarker("add:user-action");
    } catch (e) {
      console.debug(
        "Performance start mark déjà présente ou erreur bénigne:",
        e,
      );
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled",
      ) === "true"
    )
      return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);
    this.#applyFixedSubscriptionChoice(formData, form);

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          const id = formData.get("id");
          const afterAddAction =
            this.refs.addToCartButtonContainer?.dataset.afterAddAction ||
            "stay_on_page";

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton =
              this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector(
              ".add-to-cart-text--added",
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          if (afterAddAction === "redirect_to_cart") {
            window.location.href = Theme.routes.cart_url;
            return;
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        try {
          cartPerformance.measureFromEvent("add:user-action", event);
        } catch (e) {
          console.warn(
            "Impossible de mesurer add:user-action (start manquant?):",
            e,
          );
        }
      });
  }

  /**
   * If a fixed subscription option is selected, override the product variant and selling plan
   * sent by the current product form.
   *
   * @param {FormData} formData
   * @param {HTMLFormElement} form
   */
  #applyFixedSubscriptionChoice(formData, form) {
    const selectedInForm = form.querySelector(
      "[data-subscription-choice-input]:checked",
    );
    const scopedRoot =
      this.closest(
        ".quick-add-modal, dialog, .shopify-section, product-card",
      ) || document;
    const selectedByFormAttr = form.id
      ? scopedRoot.querySelector(
          `[data-subscription-choice-input][form="${form.id}"]:checked`,
        )
      : null;
    const selectedSubscriptionInput = selectedInForm || selectedByFormAttr;

    if (!(selectedSubscriptionInput instanceof HTMLInputElement)) return;

    const variantId = selectedSubscriptionInput.dataset.subscriptionVariantId;
    const sellingPlanId =
      selectedSubscriptionInput.dataset.subscriptionSellingPlanId;

    if (!variantId || !sellingPlanId) return;

    formData.set("id", variantId);
    formData.set("selling_plan", sellingPlanId);

    if (this.refs.variantId) {
      this.refs.variantId.value = variantId;
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]',
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true",
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

/** @type {boolean} */
let subscriptionChoicesDelegatedBound = false;
let purchaseCombinedDelegatedBound = false;

/**
 * @param {HTMLElement} wrapper
 * @param {HTMLButtonElement} submitButton
 * @param {HTMLInputElement | null} [selectedInput]
 * @returns {HTMLFormElement | null}
 */
function resolveSubscriptionForm(wrapper, submitButton, selectedInput = null) {
  if (selectedInput instanceof HTMLInputElement && selectedInput.form) {
    return selectedInput.form;
  }

  const formId = submitButton.dataset.productFormId;
  const scope = wrapper.closest(
    ".quick-add-modal, dialog, product-form-component, .shopify-section, product-card",
  );

  if (formId && scope instanceof Element) {
    const scopedForm = scope.querySelector(`form#${CSS.escape(formId)}`);
    if (scopedForm instanceof HTMLFormElement) return scopedForm;
  }

  if (formId) {
    const globalForm = document.getElementById(formId);
    if (globalForm instanceof HTMLFormElement) return globalForm;
  }

  if (scope instanceof Element) {
    const fallbackForm = scope.querySelector(
      'form[data-type="add-to-cart-form"]',
    );
    if (fallbackForm instanceof HTMLFormElement) return fallbackForm;
  }

  return null;
}

/**
 * @param {HTMLFormElement | null} form
 * @param {boolean} shouldDisable
 */
function setSubscriptionNativeButtonState(form, shouldDisable) {
  if (!(form instanceof HTMLFormElement)) return;

  const nativeAddButton = form.querySelector('[name="add"]');
  if (!(nativeAddButton instanceof HTMLButtonElement)) return;

  if (shouldDisable) {
    nativeAddButton.disabled = true;
    nativeAddButton.setAttribute("aria-disabled", "true");
    nativeAddButton.classList.add("subscription-native-disabled");
    nativeAddButton.dataset.subscriptionForcedDisabled = "true";
    return;
  }

  if (nativeAddButton.dataset.subscriptionForcedDisabled === "true") {
    nativeAddButton.disabled = false;
    nativeAddButton.removeAttribute("aria-disabled");
    nativeAddButton.classList.remove("subscription-native-disabled");
    delete nativeAddButton.dataset.subscriptionForcedDisabled;
  }
}

/**
 * @param {HTMLElement} wrapper
 */
function syncSubscriptionChoiceWrapper(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;

  const submitButton = wrapper.querySelector("[data-subscription-submit]");
  const errorNode = wrapper.querySelector("[data-subscription-error]");
  if (!(submitButton instanceof HTMLButtonElement)) return;

  const selectedNode = wrapper.querySelector(
    "[data-subscription-choice-input]:checked",
  );
  const selectedInput =
    selectedNode instanceof HTMLInputElement ? selectedNode : null;
  const hasSelection = selectedInput instanceof HTMLInputElement;
  const form = resolveSubscriptionForm(wrapper, submitButton, selectedInput);

  submitButton.hidden = !hasSelection;
  setSubscriptionNativeButtonState(form, hasSelection);

  if (!hasSelection && errorNode instanceof HTMLElement) {
    errorNode.textContent = "";
    errorNode.classList.add("hidden");
  }
}

/**
 * @param {Document | Element} [scope=document]
 */
function syncAllSubscriptionChoiceWrappers(scope = document) {
  scope.querySelectorAll(".subscription-choices").forEach((wrapper) => {
    if (wrapper instanceof HTMLElement) {
      syncSubscriptionChoiceWrapper(wrapper);
    }
  });
}

if (!subscriptionChoicesDelegatedBound) {
  subscriptionChoicesDelegatedBound = true;

  syncAllSubscriptionChoiceWrappers(document);

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const input = target.closest("[data-subscription-choice-input]");
    if (!(input instanceof HTMLInputElement)) return;

    const wrapper = input.closest(".subscription-choices");
    if (!(wrapper instanceof HTMLElement)) return;

    syncSubscriptionChoiceWrapper(wrapper);
  });

  document.addEventListener("click", async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const submitButton = target.closest("[data-subscription-submit]");
    if (!(submitButton instanceof HTMLButtonElement)) return;

    const wrapper = submitButton.closest(".subscription-choices");
    if (!(wrapper instanceof HTMLElement)) return;

    const errorNode = wrapper.querySelector("[data-subscription-error]");
    const selectedInput = wrapper.querySelector(
      "[data-subscription-choice-input]:checked",
    );

    if (!(selectedInput instanceof HTMLInputElement)) {
      if (errorNode instanceof HTMLElement) {
        errorNode.textContent = submitButton.dataset.selectOptionError || "";
        errorNode.classList.remove("hidden");
      }
      return;
    }

    const variantId = selectedInput.dataset.subscriptionVariantId;
    const sellingPlanId = selectedInput.dataset.subscriptionSellingPlanId;
    if (!variantId || !sellingPlanId) {
      if (errorNode instanceof HTMLElement) {
        errorNode.textContent = submitButton.dataset.invalidOptionError || "";
        errorNode.classList.remove("hidden");
      }
      return;
    }

    if (errorNode instanceof HTMLElement) {
      errorNode.textContent = "";
      errorNode.classList.add("hidden");
    }

    const form = resolveSubscriptionForm(wrapper, submitButton, selectedInput);
    const quantityInput =
      form instanceof HTMLFormElement
        ? form.querySelector('[name="quantity"]')
        : null;
    const quantity =
      quantityInput instanceof HTMLInputElement
        ? Math.max(1, Number(quantityInput.value) || 1)
        : 1;

    const initialText = submitButton.textContent || "";
    submitButton.disabled = true;
    submitButton.textContent = submitButton.dataset.loadingLabel || initialText;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity,
              selling_plan: Number(sellingPlanId),
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error("cart_add_failed");
      }

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              source: "product-form-component",
              itemCount: quantity,
            },
          },
        }),
      );

      window.location.href = Theme.routes.cart_url;
    } catch (error) {
      if (errorNode instanceof HTMLElement) {
        errorNode.textContent = submitButton.dataset.addError || "";
        errorNode.classList.remove("hidden");
      }

      submitButton.disabled = false;
      submitButton.textContent =
        submitButton.dataset.defaultLabel || initialText;
    }
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (
          node.matches(".subscription-choices") &&
          node instanceof HTMLElement
        ) {
          syncSubscriptionChoiceWrapper(node);
          return;
        }

        syncAllSubscriptionChoiceWrappers(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

/**
 * @param {HTMLElement} wrapper
 * @returns {HTMLInputElement | null}
 */
function getCombinedSelectedVariantInput(wrapper) {
  const selectedNode = wrapper.querySelector(
    ".variant-picker [data-variant-id]:checked, .variant-picker select option:checked[data-variant-id]",
  );
  if (selectedNode instanceof HTMLInputElement) return selectedNode;
  return null;
}

/**
 * @param {HTMLElement} wrapper
 * @returns {string | null}
 */
function getCombinedVariantId(wrapper) {
  const selectedInput = getCombinedSelectedVariantInput(wrapper);
  const selectedVariantId = selectedInput?.dataset.variantId;
  if (selectedVariantId) return selectedVariantId;

  const selectedOption = wrapper.querySelector(
    ".variant-picker select option:checked",
  );
  if (selectedOption instanceof HTMLOptionElement) {
    const optionVariantId = selectedOption.dataset.variantId;
    if (optionVariantId) return optionVariantId;
  }

  const stateVariantId = wrapper.dataset.currentVariantId;
  return stateVariantId || null;
}

/**
 * @param {HTMLElement} wrapper
 */
function getCombinedSubscriptionSelection(wrapper) {
  const frequencySelect = wrapper.querySelector(
    "[data-subscription-frequency]",
  );
  if (
    frequencySelect instanceof HTMLSelectElement &&
    frequencySelect.value !== ""
  ) {
    const selectedOption = frequencySelect.selectedOptions[0];
    return {
      hasSelection: true,
      variantId: selectedOption?.dataset.subscriptionVariantId || null,
      sellingPlanId: selectedOption?.dataset.subscriptionSellingPlanId || null,
      price: selectedOption?.dataset.subscriptionPrice || "",
      compareAtPrice: selectedOption?.dataset.subscriptionCompareAtPrice || "",
    };
  }

  const selectedPill = wrapper.querySelector(
    "[data-subscription-frequency-pill]:checked",
  );
  if (selectedPill instanceof HTMLInputElement) {
    return {
      hasSelection: true,
      variantId: selectedPill.dataset.subscriptionVariantId || null,
      sellingPlanId: selectedPill.dataset.subscriptionSellingPlanId || null,
      price: selectedPill.dataset.subscriptionPrice || "",
      compareAtPrice: selectedPill.dataset.subscriptionCompareAtPrice || "",
    };
  }

  return {
    hasSelection: false,
    variantId: null,
    sellingPlanId: null,
    price: "",
    compareAtPrice: "",
  };
}

/**
 * @param {string} value
 */
function escapeCombinedPriceText(value) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * @param {HTMLElement} wrapper
 * @returns {HTMLElement | null}
 */
function getCombinedProductPriceContainer(wrapper) {
  const productId = wrapper.dataset.productId;
  if (!productId) return null;

  /** @type {Array<Element | Document>} */
  const scopes = [];

  const closestDialog = wrapper.closest("dialog");
  if (closestDialog instanceof Element) {
    scopes.push(closestDialog);
  }

  const closestSection = wrapper.closest(".shopify-section");
  if (closestSection instanceof Element && !scopes.includes(closestSection)) {
    scopes.push(closestSection);
  }

  const closestProductContext = wrapper.closest(
    "product-info, product-details, .product, .product__info-wrapper, .product__info",
  );
  if (
    closestProductContext instanceof Element &&
    !scopes.includes(closestProductContext)
  ) {
    scopes.push(closestProductContext);
  }

  scopes.push(document);

  for (const scope of scopes) {
    const byProductId = scope.querySelector(
      `product-price[data-product-id="${productId}"] [ref="priceContainer"]`,
    );
    if (byProductId instanceof HTMLElement) return byProductId;

    const fallback = scope.querySelector(
      'product-price [ref="priceContainer"]',
    );
    if (fallback instanceof HTMLElement) return fallback;
  }

  return null;
}

/**
 * @param {HTMLElement} wrapper
 */
function cacheCombinedBasePriceHtml(wrapper) {
  const priceContainer = getCombinedProductPriceContainer(wrapper);
  if (!(priceContainer instanceof HTMLElement)) return;
  if (wrapper.dataset.subscriptionPriceOverridden === "true") return;
  wrapper.dataset.baseProductPriceHtml = priceContainer.innerHTML;
}

/**
 * @param {HTMLElement} wrapper
 * @param {{hasSelection:boolean, price:string, compareAtPrice:string}} subscriptionSelection
 */
function syncCombinedProductPrice(wrapper, subscriptionSelection) {
  const priceContainer = getCombinedProductPriceContainer(wrapper);
  if (!(priceContainer instanceof HTMLElement)) return;

  const displayPrice = (subscriptionSelection.price || "").trim();
  const displayCompareAtPrice = (
    subscriptionSelection.compareAtPrice || ""
  ).trim();

  if (!subscriptionSelection.hasSelection || !displayPrice) {
    if (wrapper.dataset.subscriptionPriceOverridden === "true") {
      const basePriceHtml = wrapper.dataset.baseProductPriceHtml;
      if (basePriceHtml) {
        priceContainer.innerHTML = basePriceHtml;
      }
    }
    wrapper.dataset.subscriptionPriceOverridden = "false";
    wrapper.dataset.baseProductPriceHtml = priceContainer.innerHTML;
    return;
  }

  cacheCombinedBasePriceHtml(wrapper);

  const escapedPrice = escapeCombinedPriceText(displayPrice);
  const escapedCompareAtPrice = escapeCombinedPriceText(displayCompareAtPrice);

  if (escapedCompareAtPrice) {
    priceContainer.innerHTML = `<span role="group"><span class="visually-hidden">Prix soldé&nbsp;</span><span class="price">${escapedPrice}</span></span><span role="group"><span class="visually-hidden">Prix régulier&nbsp;</span><span class="compare-at-price">${escapedCompareAtPrice}</span></span>`;
  } else {
    priceContainer.innerHTML = `<span class="price">${escapedPrice}</span>`;
  }

  wrapper.dataset.subscriptionPriceOverridden = "true";
}

/**
 * @param {HTMLElement} wrapper
 */
function clearCombinedSubscriptionSelection(wrapper) {
  const frequencySelect = wrapper.querySelector(
    "[data-subscription-frequency]",
  );
  if (frequencySelect instanceof HTMLSelectElement) {
    frequencySelect.value = "";
  }

  wrapper
    .querySelectorAll("[data-subscription-frequency-pill]:checked")
    .forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
      }
    });
}

/**
 * @param {HTMLElement} wrapper
 */
function clearCombinedVariantSelection(wrapper) {
  wrapper.querySelectorAll(".variant-picker input:checked").forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.checked = false;
    }
  });

  wrapper.querySelectorAll(".variant-picker select").forEach((select) => {
    if (select instanceof HTMLSelectElement) {
      select.selectedIndex = -1;
    }
  });

  wrapper.dataset.currentVariantId = "";
}

/**
 * @param {HTMLElement} wrapper
 */
function syncPurchaseCombinedWrapper(wrapper) {
  const oneTimeRadio = wrapper.querySelector("[data-purchase-mode-onetime]");
  const subscriptionRadio = wrapper.querySelector(
    "[data-purchase-mode-subscription]",
  );

  if (!(oneTimeRadio instanceof HTMLInputElement)) return;
  if (!(subscriptionRadio instanceof HTMLInputElement)) return;

  const subscriptionSelection = getCombinedSubscriptionSelection(wrapper);
  const hasSubscriptionSelection = subscriptionSelection.hasSelection;

  if (hasSubscriptionSelection) {
    subscriptionRadio.checked = true;
    oneTimeRadio.checked = false;
    syncCombinedProductPrice(wrapper, subscriptionSelection);
    return;
  }

  if (!oneTimeRadio.checked && !subscriptionRadio.checked) {
    oneTimeRadio.checked = true;
  }

  syncCombinedProductPrice(wrapper, subscriptionSelection);
}

/**
 * @param {Document | Element} [scope=document]
 */
function syncAllPurchaseCombinedWrappers(scope = document) {
  scope.querySelectorAll("[data-purchase-combined]").forEach((node) => {
    if (node instanceof HTMLElement) {
      syncPurchaseCombinedWrapper(node);
    }
  });
}

if (!purchaseCombinedDelegatedBound) {
  purchaseCombinedDelegatedBound = true;

  syncAllPurchaseCombinedWrappers(document);
  document.querySelectorAll("[data-purchase-combined]").forEach((node) => {
    if (node instanceof HTMLElement) {
      cacheCombinedBasePriceHtml(node);
    }
  });

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const wrapper = target.closest("[data-purchase-combined]");
    if (!(wrapper instanceof HTMLElement)) return;

    if (
      target.matches(
        "[data-subscription-frequency], [data-subscription-frequency-pill]",
      )
    ) {
      clearCombinedVariantSelection(wrapper);
      syncPurchaseCombinedWrapper(wrapper);
      return;
    }

    if (target.matches("[data-purchase-mode-onetime]")) {
      clearCombinedSubscriptionSelection(wrapper);
      syncPurchaseCombinedWrapper(wrapper);
      return;
    }

    if (target.matches("[data-purchase-mode-subscription]")) {
      clearCombinedVariantSelection(wrapper);
      syncPurchaseCombinedWrapper(wrapper);
      return;
    }

    if (target.matches(".variant-picker input, .variant-picker select")) {
      const frequencySelect = wrapper.querySelector(
        "[data-subscription-frequency]",
      );
      const oneTimeRadio = wrapper.querySelector(
        "[data-purchase-mode-onetime]",
      );
      const subscriptionRadio = wrapper.querySelector(
        "[data-purchase-mode-subscription]",
      );

      if (frequencySelect instanceof HTMLSelectElement) {
        frequencySelect.value = "";
      }
      clearCombinedSubscriptionSelection(wrapper);
      if (oneTimeRadio instanceof HTMLInputElement) {
        oneTimeRadio.checked = true;
      }
      if (subscriptionRadio instanceof HTMLInputElement) {
        subscriptionRadio.checked = false;
      }
      syncPurchaseCombinedWrapper(wrapper);
      return;
    }

    syncPurchaseCombinedWrapper(wrapper);
  });

  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const wrapper = target.closest("[data-purchase-combined]");
    if (!(wrapper instanceof HTMLElement)) return;

    const variantUpdateEvent =
      /** @type {CustomEvent<{resource?: {id?: string | number}}>} */ (event);
    const variantId = variantUpdateEvent.detail?.resource?.id;
    if (variantId) {
      wrapper.dataset.currentVariantId = String(variantId);
    }

    const frequencySelect = wrapper.querySelector(
      "[data-subscription-frequency]",
    );
    const oneTimeRadio = wrapper.querySelector("[data-purchase-mode-onetime]");
    const subscriptionRadio = wrapper.querySelector(
      "[data-purchase-mode-subscription]",
    );

    if (frequencySelect instanceof HTMLSelectElement) {
      frequencySelect.value = "";
    }
    clearCombinedSubscriptionSelection(wrapper);
    if (oneTimeRadio instanceof HTMLInputElement) {
      oneTimeRadio.checked = true;
    }
    if (subscriptionRadio instanceof HTMLInputElement) {
      subscriptionRadio.checked = false;
    }
    wrapper.dataset.subscriptionPriceOverridden = "false";
    cacheCombinedBasePriceHtml(wrapper);
    syncPurchaseCombinedWrapper(wrapper);
  });

  document.addEventListener("click", async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const submitButton = target.closest("[data-purchase-combined-submit]");
    if (!(submitButton instanceof HTMLButtonElement)) return;

    const wrapper = submitButton.closest("[data-purchase-combined]");
    if (!(wrapper instanceof HTMLElement)) return;

    const errorNode = wrapper.querySelector("[data-purchase-combined-error]");
    const subscriptionSelection = getCombinedSubscriptionSelection(wrapper);

    const quantityInput = wrapper.querySelector('input[name="quantity"]');
    const quantity =
      quantityInput instanceof HTMLInputElement
        ? Math.max(1, Number(quantityInput.value) || 1)
        : 1;

    let variantId = null;
    let sellingPlanId = null;

    if (subscriptionSelection.hasSelection) {
      variantId = subscriptionSelection.variantId;
      sellingPlanId = subscriptionSelection.sellingPlanId;
    } else {
      variantId = getCombinedVariantId(wrapper);
    }

    if (!variantId || (subscriptionSelection.hasSelection && !sellingPlanId)) {
      if (errorNode instanceof HTMLElement) {
        errorNode.textContent =
          submitButton.dataset.invalidSelectionError || "";
        errorNode.classList.remove("hidden");
      }
      return;
    }

    if (errorNode instanceof HTMLElement) {
      errorNode.textContent = "";
      errorNode.classList.add("hidden");
    }

    const initialText = submitButton.textContent || "";
    submitButton.disabled = true;
    submitButton.textContent = submitButton.dataset.loadingLabel || initialText;

    /** @type {{id:number, quantity:number, selling_plan?: number}} */
    const itemPayload = {
      id: Number(variantId),
      quantity,
    };
    if (sellingPlanId) {
      itemPayload.selling_plan = Number(sellingPlanId);
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [itemPayload],
        }),
      });

      if (!response.ok) {
        throw new Error("cart_add_failed");
      }

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              source: "product-form-component",
              itemCount: quantity,
            },
          },
        }),
      );

      window.location.href = Theme.routes.cart_url;
    } catch (error) {
      if (errorNode instanceof HTMLElement) {
        errorNode.textContent = submitButton.dataset.addError || "";
        errorNode.classList.remove("hidden");
      }

      submitButton.disabled = false;
      submitButton.textContent =
        submitButton.dataset.defaultLabel || initialText;
    }
  });

  const combinedObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (
          node.matches("[data-purchase-combined]") &&
          node instanceof HTMLElement
        ) {
          syncPurchaseCombinedWrapper(node);
          return;
        }

        syncAllPurchaseCombinedWrappers(node);
      });
    });
  });

  combinedObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint,
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
